import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';

import type {
	ConfluenceTeamAdminControlsMode,
	useTeamPickerQuery$data,
	useTeamPickerQuery$variables,
} from './__generated__/useTeamPickerQuery.graphql.ts';

const useTeamPickerQuery = graphql`
	query useTeamPickerQuery($cloudId: ID!) {
		confluence_teamAdminControlsSettings(cloudId: $cloudId) {
			teamAdminControlsMode
		}
	}
`;

export interface TeamPickerProps {
	includeTeams: boolean;
	verifiedTeams: boolean;
	includeTeamsUpdates: boolean;
}

function modeToPickerProps(
	mode: ConfluenceTeamAdminControlsMode | null | undefined,
): TeamPickerProps {
	switch (mode) {
		case 'ALL_TEAMS':
			return { includeTeams: true, verifiedTeams: false, includeTeamsUpdates: true };
		case 'VERIFIED_TEAMS':
			return { includeTeams: true, verifiedTeams: true, includeTeamsUpdates: true };
		case null:
			return { includeTeams: false, verifiedTeams: false, includeTeamsUpdates: false };
		case undefined:
			return { includeTeams: false, verifiedTeams: false, includeTeamsUpdates: false };
		default:
			return { includeTeams: false, verifiedTeams: false, includeTeamsUpdates: true };
	}
}

export const useTeamPicker = (): { pickerProps: TeamPickerProps } => {
	const { cloudId } = useSessionData();

	const { data } = useQueryInterop<useTeamPickerQuery$data, useTeamPickerQuery$variables>(
		useTeamPickerQuery,
		{
			variables: { cloudId },
			onError: (error) => {
				markErrorAsHandled(error);
			},
			skip: !expValEquals('cc_enable_admin_controls_for_teams', 'isEnabled', true),
		},
	);

	const pickerProps = useMemo(() => {
		// If admin controls data is available (mode is a valid string), use it to determine team picker props
		const mode = data?.confluence_teamAdminControlsSettings?.teamAdminControlsMode;
		if (mode !== undefined && mode !== null) {
			return modeToPickerProps(mode);
		}

		// Fallback: If TaP is enabled but no admin controls data, show all teams
		if (fg('confluence_frontend_perms_exp_teams_as_principals')) {
			return { includeTeams: true, verifiedTeams: false, includeTeamsUpdates: true };
		}

		// TaP is disabled - no teams
		return { includeTeams: false, verifiedTeams: false, includeTeamsUpdates: false };
	}, [data?.confluence_teamAdminControlsSettings?.teamAdminControlsMode]);

	return { pickerProps };
};
